import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';

import { MFA_CANCELLED_ERROR } from '@shipstation/client-common/mfa/mfa-constants';
import { mfaRequiredEmitter } from '@shipstation/client-common/mfa/mfa-emitter';
import { mfaMessageSets, mfaMessages, nativeAuthMessages } from '@shipstation/client-common/mfa/mfa-messages';

import { noop } from 'ui-components/default-props/noop';

import { showVerifyingFailed, showVerifyingInProgressNotification, showVerifyingSuccess } from './notifier';
import { StepUpAuthContextProvider } from './step-up-auth-provider';
import { trackMfaSuccess } from './tracker';
import { useAuth0MfaVerificationRequired, useNativeVerificationRequired } from './use-mfa-verification-required';
import { VerifyEmailModal } from '../../../components/verify-email-modal';
import { Auth0Wrapper } from '../../../services/auth0/Auth0Wrapper';

import type { MfaRetryRequest } from '@shipstation/client-common/mfa/mfa-types';

export type UpdateMfaClaimContextValue = {
  closeMfaModal: () => void;
  openMfaModal: (requestUrl: string) => void;
  closeNativeVerificationModal: () => void;
  openNativeVerificationModal: (source: string) => void;
};

export const UpdateMfaClaimContext = createContext<UpdateMfaClaimContextValue>({
  closeMfaModal: noop,
  openMfaModal: noop,
  closeNativeVerificationModal: noop,
  openNativeVerificationModal: noop,
});

export const useUpdateMfaClaimContext = (): UpdateMfaClaimContextValue => useContext(UpdateMfaClaimContext);

/**
 * Adapter replacement for `../update-mfa-claim-context.jsx`, behind the
 * `enableClientCommonStepUpAuth` flag. Queues requests that failed with an
 * MFA-required 403, drives the step-up modal, then replays the queue.
 */
export const UpdateMfaClaimContextProvider = ({ children }: { children?: React.ReactNode }) => {
  const requestQueueRef = useRef<MfaRetryRequest[]>([]);
  const isModalOpenRef = useRef(false);

  const retryAllQueuedRequests = useCallback(() => {
    const queue = [...requestQueueRef.current];
    requestQueueRef.current = [];

    queue.forEach(async ({ config, resolve, reject }) => {
      try {
        // oxlint-disable-next-line no-param-reassign
        delete (config as { ss_isMfaRetry?: boolean }).ss_isMfaRetry;

        // Bypass transformRequest to avoid double-stringification: the data is
        // already transformed from the original request.
        const retryConfig = {
          ...config,
          transformRequest: [],
        };

        const response = await axios.request(retryConfig);
        resolve(response);
      } catch (error) {
        reject(error);
      }
    });
  }, []);

  const clearRequestQueue = useCallback(() => {
    const queue = [...requestQueueRef.current];
    requestQueueRef.current = [];

    queue.forEach(({ reject }) => {
      reject(new Error(MFA_CANCELLED_ERROR));
    });
  }, []);

  const { openMfaModal, closeMfaModal, requestUrl, isMfaModalOpen } = useAuth0MfaVerificationRequired();

  const { nativeVerificationSource, openNativeVerificationModal, closeNativeVerificationModal } = useNativeVerificationRequired();

  const onMfaSuccess = useCallback(async () => {
    const notificationId = showVerifyingInProgressNotification();
    isModalOpenRef.current = false;
    try {
      const shouldSkipCache = true;
      await Auth0Wrapper.getAccessToken(shouldSkipCache);
      showVerifyingSuccess(notificationId);

      retryAllQueuedRequests();
      closeMfaModal();

      trackMfaSuccess({ source: requestUrl });
    } catch {
      showVerifyingFailed(notificationId, onMfaSuccess);
    }
  }, [requestUrl, closeMfaModal, retryAllQueuedRequests]);

  const onMfaModalClose = useCallback(() => {
    closeMfaModal();
    clearRequestQueue();
    isModalOpenRef.current = false;
  }, [closeMfaModal, clearRequestQueue]);

  const onNativeVerificationSuccess = useCallback(() => {
    retryAllQueuedRequests();
  }, [retryAllQueuedRequests]);

  const onNativeModalClose = useCallback(() => {
    closeNativeVerificationModal();
    clearRequestQueue();
    isModalOpenRef.current = false;
  }, [closeNativeVerificationModal, clearRequestQueue]);

  useEffect(() => {
    // G8 — MFA and native email verification are multiplexed onto one queue and
    // distinguished by `isNative`. Email verification is deliberately not unified
    // with shipstation-lite in this migration.
    return mfaRequiredEmitter.subscribe((request) => {
      requestQueueRef.current.push(request);
      if (isModalOpenRef.current) {
        return;
      }
      isModalOpenRef.current = true;

      if (request.isNative) {
        openNativeVerificationModal(request.url ?? '');
      } else {
        openMfaModal(request.url ?? '');
      }
    });
  }, [openMfaModal, openNativeVerificationModal]);

  useEffect(() => {
    return () => {
      clearRequestQueue();
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => {
    return {
      closeMfaModal,
      openMfaModal,
      closeNativeVerificationModal,
      openNativeVerificationModal,
    };
  }, [closeMfaModal, openMfaModal, closeNativeVerificationModal, openNativeVerificationModal]);

  return (
    <UpdateMfaClaimContext.Provider value={value}>
      {isMfaModalOpen ? (
        <StepUpAuthContextProvider
          isModalOpen={isMfaModalOpen}
          messages={mfaMessageSets.lastMfaClaimMessages}
          onClose={onMfaModalClose}
          onStepUpAuthSuccess={onMfaSuccess}
          source={requestUrl}
          stepUpAuthModalTitle={mfaMessages.mfaModalTitle}
        />
      ) : null}
      {nativeVerificationSource ? (
        <VerifyEmailModal
          buttonText={nativeAuthMessages.primaryAction}
          closeUnverifiedModal={onNativeModalClose}
          onSuccess={onNativeVerificationSuccess}
          source={nativeVerificationSource}
        />
      ) : null}
      {children}
    </UpdateMfaClaimContext.Provider>
  );
};
